import {
  Children,
  cloneElement,
  createElement,
  isValidElement,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import ChatUuidPill from "./ChatUuidPill";

/** RFC 4122 UUID v4 — version nibble 4, variant 8/9/a/b. */
const UUID_V4_SOURCE =
  "\\b[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\\b";

const URLISH_PREV = /[/.=?#&]/;
const SKIP_TAGS = new Set(["code", "pre", "a", "button"]);

function uuidRe(): RegExp {
  return new RegExp(UUID_V4_SOURCE, "gi");
}

/** Split a plain string, replacing UUIDv4s with copyable pills. */
export function splitTextWithUuidPills(text: string): ReactNode {
  const re = uuidRe();
  const nodes: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let n = 0;
  while ((match = re.exec(text))) {
    const prev = match.index > 0 ? text[match.index - 1] : "";
    if (prev && URLISH_PREV.test(prev)) continue;
    if (match.index > last) nodes.push(text.slice(last, match.index));
    nodes.push(<ChatUuidPill key={`uuid-${match.index}-${n++}`} value={match[0]} />);
    last = match.index + match[0].length;
  }
  if (last === 0) return text;
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Walk markdown children and pill-ify UUID strings. Leaves code/links alone. */
export function injectUuidPills(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === "string") return splitTextWithUuidPills(child);
    if (!isValidElement<{ children?: ReactNode }>(child)) return child;
    const type = child.type;
    if (typeof type === "string" && SKIP_TAGS.has(type)) return child;
    if (child.props.children == null) return child;
    return cloneElement(child, undefined, injectUuidPills(child.props.children));
  });
}

type UuidTagProps = { children?: ReactNode; node?: unknown } & HTMLAttributes<HTMLElement>;

function uuidAware(tag: keyof HTMLElementTagNameMap) {
  return function UuidAwareTag({ children, node: _node, ...props }: UuidTagProps) {
    return createElement(tag, props, injectUuidPills(children));
  };
}

export const UUID_MARKDOWN_COMPONENTS = {
  p: uuidAware("p"),
  li: uuidAware("li"),
  td: uuidAware("td"),
  th: uuidAware("th"),
  h1: uuidAware("h1"),
  h2: uuidAware("h2"),
  h3: uuidAware("h3"),
  h4: uuidAware("h4"),
  h5: uuidAware("h5"),
  h6: uuidAware("h6"),
  blockquote: uuidAware("blockquote"),
};

export function ChatTextWithUuidPills({ text }: { text: string }) {
  return <>{splitTextWithUuidPills(text)}</>;
}
